import { Dialog, Grow, LinearProgress } from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import { type FC, forwardRef } from "react";
import type { DialogController } from "../../../../../../utils/hooks/use-dialog";
import type { UUID } from "../../../../../../utils/uuid";
import { useMomentPreviewData } from "./hooks/use-moment-preview-data";
import { ReviewDialog } from "./preview-body/review-dialog";
import { PreviewContextProvider } from "./preview-context";
import { PreviewInner } from "./preview-inner";

type Props = {
  momentId: UUID;
  dialog: DialogController<{ isReview: boolean }>;
  buttons?: {
    sendTest?: React.ReactNode;
    publish?: React.ReactNode;
  };
};

export const PreviewDialog: FC<Props> = ({ dialog, momentId, ...rest }) => {
  return (
    <Dialog
      open={dialog.open}
      onClose={dialog.handleClose}
      fullScreen
      slots={{ transition: Transition }}
    >
      <PreviewDialogContent dialog={dialog} momentId={momentId} {...rest} />
      {dialog.data?.isReview && <ReviewDialog momentId={momentId} />}
    </Dialog>
  );
};

const PreviewDialogContent: FC<Props> = ({ dialog, momentId, ...rest }) => {
  const { data, loading } = useMomentPreviewData(momentId);

  if (loading) return <LinearProgress />;

  if (!data) return null;

  return (
    <PreviewContextProvider data={data}>
      <PreviewInner dialog={dialog} momentId={momentId} {...rest} />
    </PreviewContextProvider>
  );
};

const Transition = forwardRef(function TransitionFn(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>,
) {
  return <Grow ref={ref} {...props} />;
});
